import {
  AreaType,
  CrashSeverity,
  IntxTypeSignalOrStop,
  SafetyIntxType,
  SignalOrStopRampTerminal,
  TurnControlType,
} from "../SPFConstants";

/**
 * @return proportion of total leg AADT on cross road leg outside of interchange
 */
export const getpOut = ({ aadtin, aadtout, aadtex, aadten }: any): number => {
  return aadtout / (aadtin + aadtout + aadten + aadtex);
};
/**
 * @return proportion of total leg AADT on cross road leg inside of interchange
 */
export const getpIn = ({ aadtin, aadtout, aadtex, aadten }: any): number => {
  return aadtin / (aadtin + aadtout + aadten + aadtex);
};

/**
 * @return Wmein - width of median adjacent to turnlane or bay for inside crossroad leg
 */
export const getWmeIn = ({
  crossroadMedianWidth,
  insideApproachWidthOfLaneOrBay,
}: any): number => {
  return crossroadMedianWidth - insideApproachWidthOfLaneOrBay;
};

export const getpEx = ({ aadtin, aadtout, aadtex, aadten }: any): number => {
  return aadtex / (aadtin + aadtout + aadtex + aadten);
  //AADTen-AADT for the entrance ramp
  //AADTex-AADT for the exit ramp
  //AADTin-AADT for the crossroad between ramps
  //AADTout-AADT for the crossroad leg outside freeway
};
//nex - number of lanes serving exit ramp lanes
//nex,eff - effective number of lanes serving

interface getNexEffParams {
  exitRampApproachNumLanes: number;
  exitRampApproachRightTurnControlType: TurnControlType;
}
const getnEXeff = ({
  exitRampApproachNumLanes,
  exitRampApproachRightTurnControlType,
}: getNexEffParams): number => {
  switch (exitRampApproachRightTurnControlType) {
    case TurnControlType.SIGNAL:
    case TurnControlType.STOP:
    case TurnControlType.YIELD:
      return 0.5 * exitRampApproachNumLanes;
    case TurnControlType.MERGE:
    case TurnControlType.FREE:
      return 0.5 * (exitRampApproachNumLanes - 1) + 1;
    default:
      throw new Error(
        `Could not get nEXeff if turn control type is ${exitRampApproachRightTurnControlType}`
      );
  }
};

const EXIT_RAMP_CAPACITY = {
  [SafetyIntxType.DIAMOND_WITH_SIGNALS]: {
    a: 0.0668,
    c: 0.001,
  },
  [SafetyIntxType.DIAMOND_WITH_STOP_CONTROL]: {
    a: 0.151,
    c: 0.001,
  },
};

export const getCMF10 = ({
  aadtex,
  aadtin,
  aadten,
  aadtout,
  exitRampApproachNumLanes,
  exitRampApproachRightTurnControlType,
  intxType,
}: SignalOrStopRampTerminal) => {
  const pEx = getpEx({ aadtex, aadtin, aadten, aadtout });
  const nEXeff = getnEXeff({
    exitRampApproachNumLanes,
    exitRampApproachRightTurnControlType,
  });
  const { a, c } = EXIT_RAMP_CAPACITY[intxType];
  return (1 - pEx) * 1 + pEx * Math.exp((a * c * aadtex) / nEXeff);
};

interface getCoefficientAProps {
  areaType: AreaType;
  crashSeverity: CrashSeverity.FATAL_INJURY | CrashSeverity.PDO;
  intxType: IntxTypeSignalOrStop;
}
const getCoefficientAForCmf11 = ({
  areaType,
  crashSeverity,
  intxType,
}: getCoefficientAProps): number => {
  const CMFCoeffcientsforCrossRoadLeftTurnLaneFI = {
    [SafetyIntxType.DIAMOND_WITH_SIGNALS]: {
      [AreaType.URBAN]: { a: 0.65 },
      [AreaType.RURAL]: { a: 0.44 },
    },
    [SafetyIntxType.DIAMOND_WITH_STOP_CONTROL]: {
      [AreaType.URBAN]: { a: 0.59 },
      [AreaType.RURAL]: { a: 0.36 },
    },
  };

  const CMFCoeffcientsforCrossRoadLeftTurnLanePDO = {
    [SafetyIntxType.DIAMOND_WITH_SIGNALS]: {
      [AreaType.URBAN]: { a: 0.68 },
      [AreaType.RURAL]: { a: 0.66 },
    },
    [SafetyIntxType.DIAMOND_WITH_STOP_CONTROL]: {
      [AreaType.URBAN]: { a: 0.58 },
      [AreaType.RURAL]: { a: 0.55 },
    },
  };
  if (crashSeverity === CrashSeverity.FATAL_INJURY) {
    return CMFCoeffcientsforCrossRoadLeftTurnLaneFI[intxType][areaType].a;
  }
  if (crashSeverity === CrashSeverity.PDO) {
    return CMFCoeffcientsforCrossRoadLeftTurnLanePDO[intxType][areaType].a;
  }
  throw new Error(
    `Cannot find coefficient A for ${areaType} ${crashSeverity} ${intxType}`
  );
};

export const getCMF11 = ({
  aadtex,
  aadtin,
  aadten,
  aadtout,
  areaType,
  crashSeverity,
  intxType,
  isInsideApproachLaneOrBayPresent,
}: SignalOrStopRampTerminal) => {
  if (isInsideApproachLaneOrBayPresent) {
    const pIn = getpIn({ aadtin, aadtout, aadten, aadtex });
    const a = getCoefficientAForCmf11({ areaType, crashSeverity, intxType });
    return (1 - pIn) * 1 + pIn * a;
  }
  return 1;
};

const getCoefficientAForCmf12 = ({
  areaType,
  crashSeverity,
  intxType,
}: getCoefficientAProps): number => {
  const CMFCoeffcientsforCrossRoadRightTurnLaneFI = {
    [SafetyIntxType.DIAMOND_WITH_SIGNALS]: {
      [AreaType.URBAN]: { a: 0.76 },
      [AreaType.RURAL]: { a: 0.59 },
    },
    [SafetyIntxType.DIAMOND_WITH_STOP_CONTROL]: {
      [AreaType.URBAN]: { a: 0.87 },
      [AreaType.RURAL]: { a: 0.76 },
    },
  };

  const CMFCoeffcientsforCrossRoadRightTurnLanePDO = {
    [SafetyIntxType.DIAMOND_WITH_SIGNALS]: {
      [AreaType.URBAN]: { a: 0.94 },
      [AreaType.RURAL]: { a: 0.97 },
    },
    [SafetyIntxType.DIAMOND_WITH_STOP_CONTROL]: {
      [AreaType.URBAN]: { a: 0.69 },
      [AreaType.RURAL]: { a: 0.63 },
    },
  };

  if (crashSeverity === CrashSeverity.FATAL_INJURY) {
    return CMFCoeffcientsforCrossRoadRightTurnLaneFI[intxType][areaType].a;
  }
  if (crashSeverity === CrashSeverity.PDO) {
    return CMFCoeffcientsforCrossRoadRightTurnLanePDO[intxType][areaType].a;
  }
  throw new Error(
    `Cannot find coefficient A for ${areaType} ${crashSeverity} ${intxType}`
  );
};

export const getCMF12 = ({
  aadten,
  aadtex,
  aadtin,
  aadtout,
  areaType,
  crashSeverity,
  intxType,
  isOutsideApproachLaneOrBayPresent,
}: SignalOrStopRampTerminal) => {
  if (isOutsideApproachLaneOrBayPresent) {
    const pOut = getpOut({ aadtin, aadtout, aadtex, aadten });
    const a = getCoefficientAForCmf12({ areaType, crashSeverity, intxType });
    return (1 - pOut) * 1 + pOut * a;
  }
  return 1;
};

export const getCMF14 = ({
  crashSeverity,
  distanceToAdjacentRampTerminal: Lrmp,
  distanceToPublicStreetIntx: Lstr,
  intxType,
}: SignalOrStopRampTerminal) => {
  if (
    crashSeverity === CrashSeverity.PDO &&
    intxType === SafetyIntxType.DIAMOND_WITH_STOP_CONTROL
  ) {
    throw new Error(
      `CMF 14 should not exist for diamonds with stop control and PDO crashes`
    );
  }

  const CMFCoefficientForSegmentLengthFI = {
    [SafetyIntxType.DIAMOND_WITH_SIGNALS]: {
      a: -0.0185,
    },
    [SafetyIntxType.DIAMOND_WITH_STOP_CONTROL]: {
      a: -0.0141,
    },
  };
  const { a } =
    crashSeverity === CrashSeverity.FATAL_INJURY
      ? CMFCoefficientForSegmentLengthFI[intxType]
      : { a: -0.0186 };
  return Math.exp(a * (1 / Lrmp + 1 / Lstr - 0.333));
};

/**
 * @return proportion of total leg AADT on crossroad leg between ramps
 */
const getPIn = ({ aadtin, aadtout, aadtex, aadten }: any): number => {
  return aadtin / (aadtin + aadtout + aadten + aadtex);
};

export const getCMF15 = ({
  aadten,
  aadtex,
  aadtin: aadtinRaw,
  aadtout: aadtoutRaw,
  crashSeverity,
  crossroadMedianWidth: Wm,
  insideApproachWidthOfLaneOrBay: Wb,
  intxType,
}: SignalOrStopRampTerminal) => {
  if (
    intxType === SafetyIntxType.DIAMOND_WITH_STOP_CONTROL &&
    crashSeverity === CrashSeverity.PDO
  ) {
    throw new Error(
      `Cannot get CMF 15 for PDO crashes for a diamond with stop control`
    );
  }

  //Wmein - width of median adjacent to turn lane or bay for inside crossroad leg
  const WmeIn = Math.max(Wm - Math.max(Wb, 12), 0);
  const WmeOut = Wm - 12;
  const coefficientsFI = {
    [SafetyIntxType.DIAMOND_WITH_SIGNALS]: { a: 0.0287, b: -0.00074, c: 0.001 },
    [SafetyIntxType.DIAMOND_WITH_STOP_CONTROL]: {
      a: -0.0322,
      b: 0.00354,
      c: 0.001,
    },
  };
  const { a, b, c } =
    crashSeverity === CrashSeverity.FATAL_INJURY
      ? coefficientsFI[intxType]
      : { a: 0.061, b: -0.00246, c: 0.001 };
  interface minMax {
    value: number;
    min: number;
    max: number;
  }
  const getMinMax = ({ value, min, max }: minMax): number => {
    return Math.min(Math.max(value, min), max);
  };
  const aadtin =
    intxType === SafetyIntxType.DIAMOND_WITH_SIGNALS
      ? getMinMax({ value: aadtinRaw, min: 14000, max: 60000 }) // aadtin must be between 14000 and 60000 according to HSM comment
      : getMinMax({ value: aadtinRaw, min: 0, max: 14000 }); //aadtin and aadtout should be set between 0 to 14000 for oneway stop
  const aadtout =
    intxType === SafetyIntxType.DIAMOND_WITH_SIGNALS
      ? getMinMax({ value: aadtoutRaw, min: 14000, max: 60000 })
      : getMinMax({ value: aadtoutRaw, min: 0, max: 14000 });

  const pIn = getPIn({
    aadtin: aadtinRaw,
    aadtout: aadtoutRaw,
    aadten,
    aadtex,
  });
  const pOut = getpOut({
    aadtin: aadtinRaw,
    aadtout: aadtoutRaw,
    aadten,
    aadtex,
  });

  return (
    ((1 - pIn) * 1 + pIn * Math.exp((a + b * c * aadtin) * WmeIn)) *
    ((1 - pOut) * 1 + pOut * Math.exp((a + b * c * aadtout) * WmeOut))
  );
};
